import React, { useState } from 'react';
import {
  VStack,
  Box,
  HStack,
  Heading,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Badge,
  Button,
  Icon,
} from '@chakra-ui/react';
import { ChevronDownIcon, ExternalLinkIcon } from '@chakra-ui/icons';

const ExampleCard = ({ example, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Accordion allowToggle>
      <AccordionItem border="none" mb={4}>
        <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" overflow="hidden">
          <AccordionButton
            _expanded={{ bg: 'gray.50' }}
            py={5}
            px={6}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <HStack spacing={4} flex={1} textAlign="left" align="center">
              <Badge
                bg="accent.blue"
                color="white"
                borderRadius="full"
                minW="40px"
                h="40px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontWeight="bold"
                fontSize="md"
              >
                {index}
              </Badge>
              <Box flex={1}>
                <Heading as="h5" size="md" color="brand.700" mb={1}>
                  {example.title}
                </Heading>
                {example.studentName && (
                  <Text fontSize="sm" color="gray.600">
                    By <Text as="span" fontWeight="600">{example.studentName}</Text>
                  </Text>
                )}
              </Box>
            </HStack>
            <ChevronDownIcon
              transform={isExpanded ? 'rotate(-180deg)' : 'rotate(0)'}
              transition="transform 0.3s"
            />
          </AccordionButton>

          <AccordionPanel pb={0} pt={0} px={0}>
            {/* Video Embed */}
            <Box bg="black" position="relative" paddingBottom="56.25%" height={0}>
              <iframe
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                src={`https://www.youtube.com/embed/${getYouTubeId(example.videoUrl)}`}
                title={example.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </Box>

            {/* Description and Details */}
            <Box p={6} bg="white" borderTop="1px solid" borderColor="gray.100">
              <VStack spacing={4} align="start">
                {example.description && (
                  <Box>
                    <Text color="gray.700" lineHeight="tall">
                      {example.description}
                    </Text>
                  </Box>
                )}

                {/* What They Built */}
                <Box w="100%">
                  <Text fontWeight="600" color="brand.700" mb={2}>
                    What They Built
                  </Text>
                  <Text color="gray.600" fontSize="sm">
                    This project demonstrates how to apply the presentation framework to a real exercise.
                  </Text>
                </Box>

                {/* Evaluation Highlights */}
                <Box w="100%">
                  <Text fontWeight="600" color="brand.700" mb={3}>
                    Evaluation Highlights
                  </Text>
                  <HStack spacing={2} flexWrap="wrap" gap={2}>
                    <Badge colorScheme="green" variant="subtle">
                      Understanding: Good
                    </Badge>
                    <Badge colorScheme="green" variant="subtle">
                      Code: Good
                    </Badge>
                    <Badge colorScheme="green" variant="subtle">
                      Demo: Good
                    </Badge>
                  </HStack>
                </Box>

                {/* Call to Action */}
                <Button
                  as="a"
                  href={example.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  colorScheme="purple"
                  size="sm"
                  rightIcon={<ExternalLinkIcon />}
                  mt={2}
                >
                  Watch on YouTube
                </Button>
              </VStack>
            </Box>
          </AccordionPanel>
        </Box>
      </AccordionItem>
    </Accordion>
  );
};

function getYouTubeId(url) {
  const regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
}

const PresentationExamples = () => {
  const examples = [
    {
      title: 'Personal information manager',
      studentName: 'Sai Neha',
      videoUrl: 'https://youtu.be/qpvaphZYiXQ',
      description: '',
    },
    {
      title: 'Student presentation (Coming soon)',
      studentName: '',
      videoUrl: '',
      description: 'We\'ll add more student presentations as the course progresses.',
      isPlaceholder: true,
    },
  ];

  return (
    <VStack spacing={6} align="stretch">
      <Text color="gray.600" fontSize="md">
        Here are some examples of student presentations following the framework. Each example shows
        how students apply their learnings and grow with each presentation.
      </Text>

      <VStack spacing={0} align="stretch">
        {examples.map((example, idx) => (
          example.isPlaceholder ? (
            <Box
              key={idx}
              bg="gray.50"
              p={6}
              borderRadius="lg"
              border="2px dashed"
              borderColor="gray.300"
              textAlign="center"
            >
              <Badge mb={3} colorScheme="gray">
                Coming Soon
              </Badge>
              <Heading as="h5" size="md" mb={2} color="gray.600">
                {example.title}
              </Heading>
              <Text color="gray.500" fontSize="sm">
                {example.description}
              </Text>
            </Box>
          ) : (
            <ExampleCard key={idx} example={example} index={idx + 1} />
          )
        ))}
      </VStack>
    </VStack>
  );
};

export default PresentationExamples;
